import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { COUNTRY_DIAL_CODES } from "@/lib/countryDialCodes";

interface CountryCityComboboxProps {
  country: string;
  city: string;
  onCountryChange: (country: string) => void;
  onCityChange: (city: string) => void;
  idPrefix?: string;
  countryLabel?: string;
  cityLabel?: string;
  required?: boolean;
}

interface CountryItem {
  id?: string;
  isoCode: string;
  name: string;
}
interface CityItem {
  name: string;
  stateCode?: string;
}

function loadFallbackCountries(): CountryItem[] {
  return Object.keys(COUNTRY_DIAL_CODES)
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({ isoCode: "", name }));
}

/**
 * Comprehensive worldwide country/city combobox backed by the
 * `country-state-city` package. Stores plain text values (country name,
 * city name) for compatibility with existing text columns.
 */
export function CountryCityCombobox({
  country,
  city,
  onCountryChange,
  onCityChange,
  idPrefix = "loc",
  countryLabel = "Country",
  cityLabel = "City",
  required = false,
}: CountryCityComboboxProps) {
  const [countries, setCountries] = useState<CountryItem[]>(() => loadFallbackCountries());
  const [cities, setCities] = useState<CityItem[]>([]);
  const { data: dbCountries } = useQuery({
    queryKey: ["country-city-combobox-countries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("countries")
        .select("id, name, code")
        .order("name");
      if (error) throw error;
      return (data || []).map((c) => ({
        id: c.id,
        name: c.name,
        isoCode: c.code || "",
      })) as CountryItem[];
    },
  });

  useEffect(() => {
    if (dbCountries?.length) {
      // Merge DB countries into the comprehensive fallback list so clients
      // can be from anywhere in the world (not just business-operation countries).
      setCountries((prev) => {
        const byName = new Map<string, CountryItem>();
        for (const c of prev) byName.set(c.name, c);
        for (const c of dbCountries) {
          const existing = byName.get(c.name);
          if (existing) {
            byName.set(c.name, { ...existing, id: c.id, isoCode: c.isoCode || existing.isoCode });
          } else {
            byName.set(c.name, c);
          }
        }
        return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
      });
    }
  }, [dbCountries]);

  const [loadingCities, setLoadingCities] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [cityQuery, setCityQuery] = useState("");

  useEffect(() => {
    if (countries.length === 0) {
      console.warn("[CountryCityCombobox] country list is empty");
    }
  }, [countries.length]);

  const selectedCountry = useMemo(
    () => countries.find((c) => c.name === country),
    [countries, country]
  );

  // Load cities when country changes
  useEffect(() => {
    if (!selectedCountry?.id) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    let active = true;
    const loadCities = async () => {
      try {
        const { data, error } = await supabase
          .from("cities")
          .select("name")
          .eq("country_id", selectedCountry.id)
          .order("name");
        if (!active) return;
        if (error) throw error;
        const list = data || [];
        const seen = new Set<string>();
        const deduped: CityItem[] = [];
        for (const ci of list) {
          if (!seen.has(ci.name)) {
            seen.add(ci.name);
            deduped.push({ name: ci.name });
          }
        }
        setCities(deduped);
      } catch (err) {
        if (!active) return;
        console.warn("[CountryCityCombobox] city load failed", err);
        setCities([]);
      } finally {
        if (!active) return;
        setLoadingCities(false);
      }
    };
    loadCities();
    return () => {
      active = false;
    };
  }, [selectedCountry]);

  const trimmedCityQuery = cityQuery.trim();
  const hasExactCityMatch = cities.some(
    (ci) => ci.name.toLowerCase() === trimmedCityQuery.toLowerCase()
  );

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-country`}>
          {countryLabel}
          {required ? " *" : ""}
        </Label>
        <Popover open={countryOpen} onOpenChange={setCountryOpen}>
          <PopoverTrigger asChild>
            <Button
              id={`${idPrefix}-country`}
              type="button"
              variant="outline"
              role="combobox"
              className="w-full justify-between font-normal"
            >
              {country || "Select country..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search country..." />
              <CommandList>
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  {countries.map((c) => (
                    <CommandItem
                      key={c.id || c.isoCode || c.name}
                      value={c.name}
                      onSelect={() => {
                        onCountryChange(c.name);
                        onCityChange("");
                        setCountryOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          country === c.name ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {c.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-city`}>{cityLabel}</Label>
        <Popover open={cityOpen} onOpenChange={setCityOpen}>
          <PopoverTrigger asChild>
            <Button
              id={`${idPrefix}-city`}
              type="button"
              variant="outline"
              role="combobox"
              disabled={!country}
              className="w-full justify-between font-normal"
            >
              {city ||
                (country
                  ? loadingCities
                    ? "Loading cities..."
                    : cities.length
                      ? "Select city..."
                      : "Type city name..."
                  : "Pick country first")}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput
                placeholder="Search or type city..."
                value={cityQuery}
                onValueChange={setCityQuery}
              />
              <CommandList>
                <CommandEmpty>
                  {trimmedCityQuery ? (
                    <button
                      type="button"
                      className="w-full text-left px-2 py-1.5 text-sm hover:bg-accent rounded-sm"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onCityChange(trimmedCityQuery);
                        setCityQuery("");
                        setCityOpen(false);
                      }}
                    >
                      Use "{trimmedCityQuery}"
                    </button>
                  ) : (
                    <span className="text-sm text-muted-foreground">Type a city name…</span>
                  )}
                </CommandEmpty>
                <CommandGroup>
                  {cities.map((ci) => (
                    <CommandItem
                      key={`${ci.name}-${ci.stateCode || ""}`}
                      value={ci.name}
                      onSelect={() => {
                        onCityChange(ci.name);
                        setCityQuery("");
                        setCityOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          city === ci.name ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {ci.name}
                    </CommandItem>
                  ))}
                  {trimmedCityQuery && !hasExactCityMatch && (
                    <CommandItem
                      key="__use_custom_city"
                      value={`__use__${trimmedCityQuery}`}
                      onSelect={() => {
                        onCityChange(trimmedCityQuery);
                        setCityQuery("");
                        setCityOpen(false);
                      }}
                    >
                      <Check className="mr-2 h-4 w-4 opacity-0" />
                      Use "{trimmedCityQuery}"
                    </CommandItem>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}